"use client";

import { cn } from "@repo/ui/lib/utils";
import { MessageCircle, X } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";

import { SophiaConversation } from "@/components/chat/sophia-conversation";
import { registerExpertAgentOpener } from "@/lib/support-chat";

/**
 * The expert agent's support launcher.
 *
 * It sits where Intercom's default launcher sits, on the same routes, because
 * whoever the rollout moves off Intercom has to keep seeing a way to ask for
 * help -- and because the comparison is only fair if both arms reach for
 * support in the same corner, the same way.
 *
 * Copy says "Sophia": the persona is what the person knows. The code around it
 * says expert agent, which is what the repo calls the system.
 */
/** The exit transition is 220ms; this is the backstop when it never runs. */
const EXIT_BACKSTOP_MS = 400;

export function ExpertAgentWidget({
  sharesTheCorner = false,
}: {
  /**
   * Something else already owns the bottom-right corner here.
   *
   * True on the apply funnel, which has its own fixed controls there: the
   * mobile alias bar, and the post-submission survey card. Support arrived
   * last, so support is what moves: it sits above the alias bar and below the
   * survey, which is transient and has something to ask.
   */
  sharesTheCorner?: boolean;
} = {}) {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const noticeId = useId();
  const launcherRef = useRef<HTMLButtonElement>(null);

  // Every close path routes through here, because closing destroys whatever
  // held focus: without this the browser drops it on `document.body` and a
  // screen-reader cursor restarts at the top of the page.
  const closePanel = useCallback(() => {
    setOpen(false);
    setClosing(true);
    launcherRef.current?.focus();
  }, []);

  const openPanel = useCallback(() => {
    setOpen(true);
    // Clears a reopen that lands mid-exit.
    setClosing(false);
  }, []);

  // In-page "Chat with support" links open this panel through the registry;
  // without it they fall back to email, since Intercom never boots beside it.
  useEffect(() => registerExpertAgentOpener(openPanel), [openPanel]);

  // `transitionend` is the fast path, not the guarantee. Reduced motion removes
  // the transition entirely, so the event never fires -- and without a backstop
  // the panel stayed mounted behind a launcher that said "closed", with the
  // thread's poll still running. Slightly longer than the exit so the event
  // wins whenever there is one to win with.
  useEffect(() => {
    if (!closing || open) {
      return;
    }
    const timer = setTimeout(() => {
      setClosing(false);
    }, EXIT_BACKSTOP_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [closing, open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closePanel();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, closePanel]);

  return (
    // Above the sidebar and its mobile sheet, and in the same corner Intercom
    // uses, so the two arms of the rollout reach for support in the same place.
    //
    // Where the funnel already owns that corner, support steps out of the way
    // rather than sitting on top: lifted clear of the alias bar, and under the
    // survey rather than over it, so neither can have its taps taken.
    <div
      className={cn(
        // `--support-bottom` is the single source for how high off the bottom
        // this sits. The panel's own max-height is derived from it below rather
        // than restated, because the first version of this lift moved the
        // container and left the height on the old number: the panel grew past
        // the top of a phone screen and took its own header with it.
        "fixed right-5 bottom-[var(--support-bottom)] flex flex-col items-end gap-5",
        sharesTheCorner
          ? "z-40 [--support-bottom:6rem] md:[--support-bottom:1.25rem]"
          : "z-[60] [--support-bottom:1.25rem]",
      )}
    >
      {(open || closing) && (
        <div
          id={noticeId}
          role="dialog"
          aria-label="Chat with Sophia"
          onTransitionEnd={(event) => {
            // Guarded on the target and the property: the bubbles and the
            // resize transition too, and only the panel's own opacity marks
            // the end of an exit.
            if (
              event.target === event.currentTarget &&
              event.propertyName === "opacity" &&
              !open
            ) {
              setClosing(false);
            }
          }}
          // Mounted only while it is on screen, so a thread nobody has asked
          // for costs no query -- the exit animation is the one moment it
          // outlives `open`. The size is a class swap on this same element:
          // rendering a second panel would remount the thread and take the
          // optimistic bubbles and paged history with it.
          className={cn(
            // Transitions rather than keyframes throughout: a keyframe restarts
            // from zero when the next toggle lands mid-flight, which is what
            // made opening and closing quickly look broken. A transition
            // retargets from wherever it is.
            //
            "bg-background origin-bottom-right flex flex-col overflow-hidden rounded-[20px] shadow-[0_0_0_1px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.06),0_12px_28px_-4px_rgba(0,0,0,0.12),0_32px_64px_-12px_rgba(0,0,0,0.20)] dark:ring-1 dark:ring-white/10",
            "transition-[width,opacity,transform] ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none",
            open
              ? "scale-100 opacity-100 duration-[320ms]"
              : "pointer-events-none scale-95 opacity-0 duration-[220ms]",
            // Height is constant: expanding is horizontal only, the way
            // Intercom's is. Growing both would move the composer under the
            // reader mid-conversation, and there is no more thread to show --
            // only more room per line.
            // 5.75rem is the launcher, the gap above it, and the breathing
            // room at the top -- everything between the panel and the viewport
            // edges that is NOT the offset. Adding the offset back gives the
            // old 7rem wherever nothing has moved.
            "h-[48rem] max-h-[calc(100dvh-var(--support-bottom)-5.75rem)]",
            expanded
              ? "w-[min(38rem,calc(100vw-2.5rem))]"
              : "w-[min(25rem,calc(100vw-2.5rem))]",
          )}
        >
          <SophiaConversation
            expanded={expanded}
            onToggleExpand={() => {
              setExpanded((wasExpanded) => !wasExpanded);
            }}
            onClose={closePanel}
          />
        </div>
      )}

      <button
        ref={launcherRef}
        type="button"
        aria-expanded={open}
        aria-controls={open ? noticeId : undefined}
        aria-label={open ? "Close Sophia" : "Chat with Sophia"}
        onClick={() => {
          if (open) {
            closePanel();
            return;
          }
          openPanel();
        }}
        className="bg-marketing-blue flex size-12 items-center justify-center rounded-full text-white shadow-[0_2px_6px_rgba(0,0,0,0.12),0_8px_20px_-4px_rgba(0,0,0,0.28)] transition-transform duration-150 ease-out select-none hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none active:scale-95 motion-reduce:transition-none motion-reduce:hover:scale-100 motion-reduce:active:scale-100"
      >
        {/* Both icons stay mounted and cross-fade in place, which is what makes
            the launcher read as one thing changing state rather than two
            buttons swapping. Rotation gives the eye the direction of travel:
            the mark turns out as the cross turns in. */}
        <span className="relative flex size-5 items-center justify-center">
          <MessageCircle
            aria-hidden
            className={cn(
              "absolute size-5 transition-[opacity,transform] duration-[260ms] ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none",
              open
                ? "scale-75 rotate-90 opacity-0"
                : "scale-100 rotate-0 opacity-100",
            )}
          />
          <X
            aria-hidden
            className={cn(
              "absolute size-5 transition-[opacity,transform] duration-[260ms] ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none",
              open
                ? "scale-100 rotate-0 opacity-100"
                : "scale-75 -rotate-90 opacity-0",
            )}
          />
        </span>
      </button>
    </div>
  );
}
